















































































import { Component, Inject, Mixins, Prop, Watch } from "vue-property-decorator";
import _ from "@/boot/lodash";

// Mixins
import HasValidation from "@/mixins/HasValidation";

// Types
import {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";

import {
  IProvisionProviderData,
  IProvisionProviderMethods
} from "@/models/providers/provisioning";
import { IBasketProvisioningFieldsForm } from "@/models/provisioning";

import { IProductConfig } from "@/models/providers/products";
import { IBasketProduct } from "@/models/baskets";
import { IProduct } from "@/models/products";

@Component({
  components: {
    "product-attributes": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productAttributes.vue"
      ),
    "product-config-provider": () =>
      import("@/components/app/global/baskets/basket/productConfigProvider"),
    "product-control": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productControl.vue"
      ),
    "product-detail": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productDetail.vue"
      ),
    "product-options": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productOptions.vue"
      ),
    "product-provision-fields": () =>
      import(
        "@/components/app/global/baskets/basket/provision/productProvisionFields.vue"
      ),
    "product-provision-provider": () =>
      import(
        "@/components/app/global/baskets/basket/provision/productProvisionProvider.vue"
      ),
    "product-terms": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productTerms.vue"
      )
  }
})
export default class BasketProductConfigureView extends Mixins(HasValidation) {
  // Inject
  @Inject({ from: "basketProviderData" })
  public readonly $basketData!: () => IBasketProviderData;

  @Inject({ from: "basketProviderMethods" })
  public readonly $basketMethods!: IBasketProviderMethods;

  // Props
  @Prop({ default: null })
  public preselectedProductId!: IProduct["id"];

  @Prop({ default: null })
  public preselectedBasketProductId!: IBasketProduct["id"];

  // Data
  public isProcessing = false;
  public productId = "";
  public basketProductId = "";

  public config: IProductConfig | null = null;
  public configClone: IProductConfig | null = null;
  public configIsInitialised = false;

  // Computed
  public get basketData() {
    return this.$basketData();
  }

  public get basketProduct() {
    const products = _.get(this.basketData, "basket.products");
    return _.find(products, ["id", this.preselectedBasketProductId]) || null;
  }

  // Life-cycle Methods

  public async created() {
    // Set basketProductId
    this.setBasketProductId();

    // Set productId
    this.setProductId();

    if (!this.productId) return this.selectProduct();
  }

  // Methods

  public handleError(error) {
    if (
      this.$store.getters["api/is403"](error) ||
      this.$store.getters["api/is404"](error) ||
      this.$store.getters["api/is409"](error)
    ) {
      this.selectProduct();
    }
    if (this.$store.getters["api/is401"](error)) {
      this.$basketMethods.forceBasketReset();
    }
  }

  public setBasketProductId(id = 0) {
    this.$set(
      this,
      "basketProductId",
      id || _.get(this.basketProduct, "id", id)
    );
  }

  public setProductId() {
    this.$set(
      this,
      "productId",
      _.get(this.basketProduct, "product_id", this.preselectedProductId)
    );
  }

  public async validateBeforeSubmit() {
    if (!this.config) return;

    try {
      this.isProcessing = true;

      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");

      // Add or update product
      const basket = !this.basketProductId
        ? await this.$basketMethods.addToBasket(this.config)
        : this.mainFormIsChanged
        ? await this.$basketMethods.updateBasketProduct(
            this.basketProductId,
            this.config
          )
        : this.basketData.basket;

      // Get newly created basket product
      const basketProductId =
        this.basketProductId ||
        _.get(_.orderBy(basket.products, ["created_at"], ["desc"])[0], "id");

      if (!this.basketProductId) {
        this.setBasketProductId(basketProductId);
      }

      // Save provisioning fields (if applicable)
      if (this.hasProvisioning && this.provisionFormIsChanged) {
        await this.$provisionMethods.updateBasketProductFields(
          basket.id,
          basketProductId,
          this.form
        );
        // Need basket data refresh
        await this.$basketMethods.getBasket(true, basket.id);
      }

      this.goToBasketView();
      this.$emit("success");
    } catch (error) {
      // Handle validation error
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public selectProduct() {
    this.$emit("select-product");
  }

  public goToBasketView() {
    this.$emit("close");
  }

  // Provisioning form
  // --------------------------------------------------------------------------------

  @Inject({ from: "provisionProviderData" })
  public readonly $provisionData!: () => IProvisionProviderData;

  @Inject({ from: "provisionProviderMethods" })
  public readonly $provisionMethods!: IProvisionProviderMethods;

  public form = {
    provision_field_values: {}
  } as IBasketProvisioningFieldsForm;
  public formClone = {
    provision_field_values: {}
  } as IBasketProvisioningFieldsForm;

  public initialiseForm() {
    this.formClone = _.cloneDeep(this.form);
  }

  public updateForm(values) {
    this.$set(this, "form", {
      provision_field_values: values
    });
  }

  public get hasProvisioning() {
    return this.$provisionData().hasProvisioning;
  }

  public get provisionFormIsChanged() {
    return !_.isEqual(
      this.form.provision_field_values,
      this.formClone.provision_field_values
    );
  }

  public get subProductIds() {
    return _.map(
      _.concat(
        _.get(this.config, "attributes", []),
        _.get(this.config, "options", [])
      ),
      (item: any) => item.product_id
    );
  }

  public get mainFormIsChanged() {
    return !_.isEqual(this.configClone, this.config);
  }

  @Watch("config", { deep: true })
  public emitNewConfig(value) {
    if (!_.isNull(value) && !this.configIsInitialised) {
      this.configClone = value;
      this.configIsInitialised = true;
    }
  }
}
